import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { catalogWriteError, type CatalogWriteError } from './catalogErrors';
import { useCatalogCache } from '../../../api/admin-vehicle-catalog';
import { publishToast } from '../../../components/feedback';

/**
 * One runner for every «Справочник авто» write (AUT-437).
 *
 * Success: `apply(result)` (e.g. put the PATCHed make into the cache), then
 * the `['admin','vehicle-catalog']` prefix is invalidated and AWAITED — the
 * detail is refetched before the runner resolves, so the next PATCH carries
 * the `updatedAt` an alias write or a model create just moved.
 *
 * Failure: the catalogue refetches too; a `toast` outcome shows one toast,
 * `notFound` calls `onNotFound` (close the drawer) with a toast; `conflict`,
 * `tooLong` and `stale` come back to the caller for the inline UI.
 */
export type CatalogWriteOutcome<T> =
  | { ok: true; result: T }
  | { ok: false; error: CatalogWriteError };

export interface UseCatalogWriteResult {
  busy: boolean;
  run: <T>(
    write: () => Promise<T>,
    options?: { successKey?: string; apply?: (result: T) => void },
  ) => Promise<CatalogWriteOutcome<T>>;
}

const TOAST_OK_MS = 4000;
const TOAST_ERROR_MS = 6000;

export const useCatalogWrite = (onNotFound?: () => void): UseCatalogWriteResult => {
  const { t } = useTranslation();
  const cache = useCatalogCache();
  const [busy, setBusy] = useState(false);

  const fail = (error: CatalogWriteError): void => {
    if (error.kind === 'notFound') {
      onNotFound?.();
      publishToast({
        severity: 'error',
        durationMs: TOAST_ERROR_MS,
        message: t('vehicleCatalog.errors.notFound'),
      });
    } else if (error.kind === 'toast') {
      publishToast({ severity: 'error', durationMs: TOAST_ERROR_MS, message: t(error.key) });
    }
  };

  const run: UseCatalogWriteResult['run'] = async (write, options) => {
    setBusy(true);
    try {
      const result = await write();
      options?.apply?.(result);
      await cache.refreshCatalog();
      if (options?.successKey) {
        publishToast({
          severity: 'success',
          durationMs: TOAST_OK_MS,
          message: t(options.successKey),
        });
      }
      return { ok: true, result };
    } catch (err: unknown) {
      const error = catalogWriteError(err);
      fail(error);
      await cache.refreshCatalog();
      return { ok: false, error };
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
